import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { Check, ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/content/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NewsletterGate } from "@/components/NewsletterGate";

export const Route = createFileRoute("/aeo-checklist")({
  head: () =>
    buildSeo({
      title: "AEO Implementation Checklist: Entity & Schema Signals",
      description:
        "Step-by-step AEO checklist: schema, entity signals, citation-worthy content, AI-bot crawlability, and measurement. Free PDF download.",
      path: "/aeo-checklist",
    ogEyebrow: "AEO Checklist",
      keywords:
        "AEO checklist, answer engine optimization checklist, AEO PDF, schema checklist, entity SEO checklist, ChatGPT SEO checklist",
    }),
  component: AeoChecklist,
});

const SECTIONS: { title: string; intro: string; items: string[] }[] = [
  {
    title: "1. Schema & Structured Data",
    intro: "Make every page machine-readable so AI engines can extract entities, relationships, and answers without guessing.",
    items: [
      "Add Organization schema with sameAs links to authoritative profiles (LinkedIn, Wikidata, Crunchbase).",
      "Add Person schema for founders/authors with jobTitle, knowsAbout, and worksFor.",
      "Use Article/BlogPosting schema with author, datePublished, dateModified, and headline.",
      "Add FAQPage schema to every page with a Q&A block — match on-page text exactly.",
      "Add HowTo schema to step-by-step content where applicable.",
      "Add Product/Service schema with offers, aggregateRating, and provider.",
      "Add BreadcrumbList schema across all non-home pages.",
      "Validate every template in Schema.org Validator + Google Rich Results Test.",
    ],
  },
  {
    title: "2. Entity Signals & Authority",
    intro: "AI engines reward entities they can disambiguate. Tighten your knowledge graph footprint.",
    items: [
      "Claim and complete a Wikidata entry for your brand and key people.",
      "Establish a consistent NAP (Name, Address, Phone) across the web.",
      "Build sameAs links across LinkedIn, Crunchbase, GitHub, X, YouTube.",
      "Get cited in 3rd-party listicles and roundups — these become AI training fodder.",
      "Publish a clear About page stating entity attributes (founded, location, expertise).",
      "Add author bio boxes with credentials and outbound links to author profiles.",
      "Maintain a /press or /media page summarizing brand mentions.",
    ],
  },
  {
    title: "3. Citation-Worthy Content",
    intro: "Write paragraphs and lists that AI engines can lift verbatim into a citation.",
    items: [
      "Write definition-first paragraphs (40–60 words) under each H2.",
      "Use clear lists, tables, and stat callouts AI engines can quote directly.",
      "Cite primary sources with outbound links — verifiability is a ranking input.",
      "Update dates, stats, and examples quarterly; refresh modifiedTime in schema.",
      "Cover the full topical cluster: pillar + 5–10 supporting articles.",
      "Answer 'what is', 'how to', 'best', and 'vs' queries explicitly.",
    ],
  },
  {
    title: "4. Crawlability for AI Bots",
    intro: "If GPTBot can't fetch you, you can't be cited. Treat AI crawlers as first-class.",
    items: [
      "Allow GPTBot, PerplexityBot, ClaudeBot, Google-Extended in robots.txt.",
      "Publish an llms.txt at /llms.txt summarizing your site for LLMs.",
      "Ensure server-rendered HTML — AI crawlers do not execute JavaScript reliably.",
      "Submit XML sitemap and keep lastmod accurate.",
      "Return clean 200 responses; eliminate redirect chains and soft 404s.",
    ],
  },
  {
    title: "5. Measurement & Tracking",
    intro: "Citations are the new rankings. Build a panel, log the data, report monthly.",
    items: [
      "Track referral traffic from chat.openai.com, perplexity.ai, gemini.google.com, copilot.microsoft.com.",
      "Run a weekly prompt panel (20–30 prompts); log citations via Profound, Otterly, or AthenaHQ.",
      "Track share-of-voice across ChatGPT, Perplexity, Gemini, and Google AI Overviews.",
      "Monitor brand mentions (linked + unlinked) — AI engines weight unlinked mentions.",
      "Report monthly: citation count, citation rank, prompt coverage %, AI referral sessions.",
    ],
  },
];

function AeoChecklist() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "AEO Checklist", path: "/aeo-checklist" }]} />
      <Section headingLevel={1}
        eyebrow="AEO Checklist"
        title={
          <>
            The AEO implementation checklist for <span className="text-gradient">getting cited by AI.</span>
          </>
        }
        subtitle="A step-by-step playbook covering schema, entity signals, citation-worthy content, crawlability, and measurement. Free PDF — no email wall."
      >
        <div className="-mt-4 max-w-xl">
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Free PDF · Drop your email</div>
          <NewsletterGate
            fileUrl="/aeo-implementation-checklist.pdf"
            promise="Send me the checklist"
            source="aeo-checklist"
          />
          <p className="text-xs text-muted-foreground mt-3">
            One email. The PDF + occasional SEO/AEO playbooks. Unsubscribe anytime.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 mt-6">
          <Link
            to="/services/aeo-geo"
            className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary transition"
          >
            See AEO & GEO services <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      <div className="container-x pb-24 grid gap-8">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.06}>
            <div className="rounded-3xl border border-border bg-card p-8 lg:p-10">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Step {i + 1}</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight">{s.title}</h2>
              <p className="mt-3 text-muted-foreground max-w-2xl">{s.intro}</p>
              <ul className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-3">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm">
                    <Check size={16} className="text-primary mt-1 flex-none" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <Section title="Want this implemented for you?">
        <Link
          to="/services/aeo-geo"
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium"
        >
          Start an AEO engagement <ArrowUpRight size={16} />
        </Link>
      </Section>

      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "AEO Implementation Checklist",
          description:
            "Step-by-step Answer Engine Optimization checklist covering schema, entity signals, content, crawlability, and measurement.",
          totalTime: "P30D",
          step: SECTIONS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            itemListElement: s.items.map((t) => ({ "@type": "HowToDirection", text: t })),
          })),
        }}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "AEO Checklist", path: "/aeo-checklist" },
        ])}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "DigitalDocument",
          name: "AEO Implementation Checklist (PDF)",
          author: { "@type": "Person", name: SITE.fullName },
          encodingFormat: "application/pdf",
          url: `${SITE.url}/aeo-implementation-checklist.pdf`,
        }}
      />
    </>
  );
}